import { supabase } from '@/integrations/supabase/client';

// Push SW lives under /push/ so its default scope (`/push/`) does not collide
// with the vite-plugin-pwa worker at `/sw.js` (scope `/`).
const SW_VERSION = '20260620-push-icons-v4';
const SW_PATH = `/push/sw.js?v=${SW_VERSION}`;
const SW_SCOPE = '/push/';
const LEGACY_SW_PATH = '/push-sw.js';
const VAPID_CACHE_KEY = 'sisthoko_vapid_public_key';

function isPreviewHost() {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h.startsWith('id-preview--') || h.startsWith('preview--')
    || h.endsWith('.lovableproject.com') || h.endsWith('.lovableproject-dev.com')
    || h.endsWith('.beta.lovable.dev');
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getVapidPublicKey(): Promise<string> {
  const cached = sessionStorage.getItem(VAPID_CACHE_KEY);
  if (cached) return cached;
  const { data, error } = await supabase.functions.invoke('get-vapid-public-key');
  if (error || !data?.publicKey) throw new Error('No VAPID public key configured');
  sessionStorage.setItem(VAPID_CACHE_KEY, data.publicKey);
  return data.publicKey as string;
}

function detectDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'Android phone';
  if (/iPhone|iPad/i.test(ua)) return 'iPhone/iPad';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  return 'Web browser';
}

export async function getPushPermission(): Promise<NotificationPermission> {
  if (!pushSupported()) return 'denied';
  return Notification.permission;
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

/** Remove the legacy /push-sw.js registration so the new SW can take over. */
export async function cleanupLegacyServiceWorker(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) {
      const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '';
      if (url.endsWith(LEGACY_SW_PATH)) {
        await r.unregister();
      }
    }
  } catch {}
}

async function markPermissionAsked(userId: string, extra: Record<string, unknown> = {}) {
  const { data: prof } = await supabase
    .from('profiles')
    .select('notification_preferences')
    .eq('id', userId)
    .maybeSingle();
  const prefs = (prof?.notification_preferences as Record<string, unknown>) || {};
  await supabase
    .from('profiles')
    .update({
      notification_preferences: {
        ...prefs,
        web_push_permission_asked: true,
        ...extra,
      },
    })
    .eq('id', userId);
}

export async function enablePush(): Promise<{ ok: boolean; error?: string }> {
  if (!pushSupported()) return { ok: false, error: 'Push notifications are not supported on this device.' };
  if (isPreviewHost()) return { ok: false, error: 'Push only works on the published app, not the preview.' };
  try {
    await cleanupLegacyServiceWorker();
    const permission = await Notification.requestPermission();

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { ok: false, error: 'You need to be signed in.' };

    if (permission !== 'granted') {
      // Record the ask so we don't pester again
      await markPermissionAsked(user.id, {
        web_push_enabled: false,
        web_push_snoozed_until: permission === 'denied'
          ? null
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
      return { ok: false, error: 'Permission was not granted.' };
    }

    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE, updateViaCache: 'none' });
    await reg.update();
    await navigator.serviceWorker.ready;
    const publicKey = await getVapidPublicKey();
    const existing = await reg.pushManager.getSubscription();
    const sub = existing ?? await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
    });
    const subData = sub.toJSON();
    if (!subData.endpoint || !subData.keys?.p256dh || !subData.keys?.auth) {
      return { ok: false, error: 'Subscription is missing keys.' };
    }

    const { error } = await supabase
      .from('user_push_subscriptions')
      .upsert({
        user_id: user.id,
        endpoint: subData.endpoint,
        p256dh_key: subData.keys.p256dh,
        auth_key: subData.keys.auth,
        user_agent: navigator.userAgent,
        device_label: detectDeviceLabel(),
        is_active: true,
        last_used_at: new Date().toISOString(),
      }, { onConflict: 'user_id,endpoint' });
    if (error) throw error;

    await markPermissionAsked(user.id, {
      web_push_enabled: true,
      web_push_snoozed_until: null,
    });
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? String(e) };
  }
}

export async function disablePushOnDevice(): Promise<{ ok: boolean; error?: string }> {
  try {
    const sub = await getCurrentSubscription();
    if (sub) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('user_push_subscriptions')
          .update({ is_active: false })
          .eq('user_id', user.id)
          .eq('endpoint', sub.endpoint);
      }
      await sub.unsubscribe();
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? String(e) };
  }
}

// Backwards-compat alias used by older callers
export const disablePush = disablePushOnDevice;
